<template>
  <div :class="{ 'mb-5': !minimal }">
    <template v-if="allDataLoaded && !loadError">
      <div
        v-if="!minimal"
        class="top-content-bar">
        <div class="wrapper">

          <div class="flex flex-align-items-center ml-auto flex-justify-content-end py-3">
            <f-button
              :disabled="!allDataLoaded"
              theme="primary"
              @click.prevent="$bus.$emit('calendar-add-meeting-popup-show')">
              {{ $t('calendar.planMeeting') }}
            </f-button>
            <f-button
              theme="warning"
              class="ml-1"
              @click.prevent="reloadMeetings()">
              {{ $t('ui.refresh') }}
            </f-button>
          </div>
        </div>
      </div>
      <div :class="{ 'wrapper mt-3': !minimal }">
        <div
          :class="{
            'calendar card': true,
            'calendar-minimal': minimal
          }">
          <div
            :class="{
              'calendar-heading': true,
              'px-2 pt-3': !minimal,
              'px-2 pb-0': minimal
            }">
            <div
              :class="{
                'calendar-period': true,
                'heading-small': !minimal,
                'heading-tiny text-center': minimal
              }">
              {{ period }}
            </div>
            <div class="calendar-nav-top">
              <a
                href="#prev-month"
                :class="{ 'calendar-nav-top-prev mr-2 mr-1-sm': true, 'is-inactive': !previousMonthIsAvailable }"
                @click.prevent="goToPreviousMonth"
                :title="previousMonthName">
              </a>

              <f-button
                outline
                theme="text"
                @click.prevent="showToday"
                :title="$t('time.today')">
                {{ $t('calendar.showToday') }}
              </f-button>

              <a
                href="#next-month"
                class="calendar-nav-top-next ml-2 ml-1-sm"
                @click.prevent="goToNextMonth"
                :title="nextMonthName">
              </a>
            </div>
          </div>

          <div
            :class="{
              'card-body px-2': true,
              'pt-2': !minimal,
              'pb-2 pt-1': minimal
            }">
            <div class="calendar-heading-days">
              <div class="calendar-heading-day"><span class="hidden-desktop-only">{{ daysNames.day1.short }}</span><span class="hidden-mobile">{{ daysNames.day1.full }}</span></div>
              <div class="calendar-heading-day"><span class="hidden-desktop-only">{{ daysNames.day2.short }}</span><span class="hidden-mobile">{{ daysNames.day2.full }}</span></div>
              <div class="calendar-heading-day"><span class="hidden-desktop-only">{{ daysNames.day3.short }}</span><span class="hidden-mobile">{{ daysNames.day3.full }}</span></div>
              <div class="calendar-heading-day"><span class="hidden-desktop-only">{{ daysNames.day4.short }}</span><span class="hidden-mobile">{{ daysNames.day4.full }}</span></div>
              <div class="calendar-heading-day"><span class="hidden-desktop-only">{{ daysNames.day5.short }}</span><span class="hidden-mobile">{{ daysNames.day5.full }}</span></div>
              <div class="calendar-heading-day"><span class="hidden-desktop-only">{{ daysNames.day6.short }}</span><span class="hidden-mobile">{{ daysNames.day6.full }}</span></div>
              <div class="calendar-heading-day"><span class="hidden-desktop-only">{{ daysNames.day7.short }}</span><span class="hidden-mobile">{{ daysNames.day7.full }}</span></div>
            </div>

            <div
              :class="{ 'calendar-days': true, 'is-loading': !allDataLoaded }">
              <f-calendar-day
                v-for="(dayData, index) of visibleDays"
                :key="'day-item-' + index"
                :class="{'day-minimal': minimal}"
                @click.native="showDayPopup(dayData.date)"
                :itemData="getMeetingsForDay(dayData.date)"
                :dayData="dayData" />
            </div>
          </div>

        </div>
      </div>

      <calendar-meeting-popup
        :allCustomers="allCustomers" />
      <calendar-day-popup />
    </template>
    <template v-else>
      <div
        v-if="!allDataLoaded && !loadError"
        class="loading-spinner-wrapper">
        <div class="loading-spinner pb-3 pt-3">
          <span class="loading-spinner-content">
            {{ $t('ui.loadingDataInProgress') }}&hellip;
          </span>
        </div>
      </div>
      <div class="wrapper mt-3">
        <f-alert
          v-if="loadError"
          visible
          theme="warning">
          <span>{{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}</span>
        </f-alert>
      </div>
    </template>
  </div>
</template>

<script>
import ListUtils from '@/utils/ListUtils.js';
import FormUtils from '@/utils/FormUtils.js';
import DaysNames from '@/data/days-names.js';
import CalendarUtils from '@/utils/CalendarUtils';
import FCalendarDay from '@/components/layout/FCalendarDay.vue';
import CalendarMeetingPopup from '@/components/popups/CalendarMeetingPopup.vue';
import CalendarDayPopup from '@/components/popups/CalendarDayPopup.vue';

export default {
  name: 'calendar',
  components: {
    'f-calendar-day': FCalendarDay,
    'calendar-meeting-popup': CalendarMeetingPopup,
    'calendar-day-popup': CalendarDayPopup
  },
  props: {
    minimal: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    allDataLoaded() {
      return this.loaded && this.customersLoaded;
    },
    daysNames () {
      return DaysNames;
    },
    period () {
      return CalendarUtils.getMonthName(this.currentMonth) + ' ' + this.currentYear;
    },
    visibleDays () {
      return CalendarUtils.getDaysList(this.currentYear, this.currentMonth);
    },
    previousMonthIsAvailable () {
      if (this.currentMonth === 1 && this.currentYear === 2016) {
        return false;
      }

      return true;
    },
    previousMonthName () {
      if (parseInt(this.currentMonth, 10) === 1) {
        return CalendarUtils.getMonthName(12) + ' ' + (this.currentYear - 1);
      }

      return CalendarUtils.getMonthName(this.currentMonth - 1) + ' ' + this.currentYear;
    },
    nextMonthName () {
      if (parseInt(this.currentMonth, 10) === 12) {
        return CalendarUtils.getMonthName(1) + ' ' + (this.currentYear + 1);
      }

      return CalendarUtils.getMonthName(this.currentMonth + 1) + ' ' + this.currentYear;
    },

  },
  data() {
    return {
      calendarItems: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        year: '',
        month: ''
      },
      currentYear: null,
      currentMonth: null,
      allCustomers: [],
      customersLoaded: false,
    };
  },
  mounted () {
    this.$bus.$on('calendar-refresh', this.reloadMeetings);
    let today = new Date();
    this.currentYear = today.getFullYear();
    this.currentMonth = today.getMonth() + 1;

    this.reloadMeetings();
    this.loadAdditionalData();
  },
  methods: {
    reloadMeetings () {
      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: '/api/adviser/meetings/items',
        listField: 'calendarItems',
        pagination: false,
        errorKey: 'loadError',
        params: {
          where: {
            start_date: this.visibleDays[0].date,
            end_date: this.visibleDays[this.visibleDays.length - 1].date,
          }
        }
      });
    },
    goToPreviousMonth () {
      let currentYear = parseInt(this.currentYear, 10);
      let currentMonth = parseInt(this.currentMonth, 10);
      currentMonth--;

      if (currentMonth === 0) {
        currentMonth = 12;
        currentYear--;
      }

      this.updatePeriod(currentYear, currentMonth);
    },
    goToNextMonth () {
      let currentYear = parseInt(this.currentYear, 10);
      let currentMonth = parseInt(this.currentMonth, 10);
      currentMonth++;

      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }

      this.updatePeriod(currentYear, currentMonth);
    },
    updatePeriod (newYear, newMonth) {
      this.loaded = false;
      this.currentYear = parseInt(newYear, 10);
      this.currentMonth = parseInt(newMonth, 10);

      this.reloadMeetings();
    },
    showToday () {
      let today = new Date();
      this.updatePeriod(today.getFullYear(), today.getMonth() + 1);
    },
    showDayPopup (date) {
      this.$bus.$emit('show-calendar-day-popup', date, this.calendarItems);
      return true;
    },
    getMeetingsForDay (date) {
      let filteredMeetings = this.calendarItems.filter(meeting => meeting.start_date <= date && meeting.end_date >= date);
      if (filteredMeetings.length) {
        return filteredMeetings;
      }
      return null;
    },
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/adviser/customers/items',
        method: 'get',
        outputKey: 'allCustomers',
        loadedKey: 'customersLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    }
  }
};
</script>


<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';

.calendar {
  &-heading {
    align-items: center;
    display: flex;
    padding: 2rem;
    position: relative;
    width: 100%;

    .calendar-period {
      text-transform: capitalize;
    }
  }

  &-heading-days {
    display: flex;
  }

  &-heading-day {
    align-items: center;
    background: $color-light;
    border: 1px solid $color-border;
    border-left: none;
    border-right: none;
    display: flex;
    font-size: var(--font-size-base);
    font-weight: 500;
    justify-content: center;
    height: 3rem;
    text-align: center;
    width: calc(100% / 7);

    &:nth-child(even) {
      background: $color-border;
      border-left: 1px solid $color-border;
      border-right: 1px solid $color-border;
      left: -1px;
      position: relative;
    }
  }

  &-days {
    display: flex;
    flex-wrap: wrap;

    &.is-loading {
      opacity: .25;
    }
  }

  &-nav {
    align-items: center;
    display: flex;
    height: 80px;
    justify-content: center;

    .button {
      justify-content: center;
      width: 150px;
    }

    &-top {
      display: flex;
      left: 50%;
      padding-top: 1px;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);

      &-prev,
      &-next {
        width: 20px;

        &:before,
        &:after {
          background: $color-primary;
          content: "";
          display: block;
          height: 2px;
          position: absolute;
          top: 50%;
          transform-origin: 0 50%;
          width: 14px;
        }

        &:hover {
          &:before,
          &:after {
            background: $color-secondary;
          }
        }
      }

      &-prev {
        &:before,
        &:after {
          left: 0;
        }

        &:before {
          transform: translateY(1px) rotate(-45deg);
        }

        &:after {
          transform: rotate(45deg);
        }

        &.is-inactive {
          opacity: .25;
          pointer-events: none;
        }
      }

      &-next {
        &:before,
        &:after {
          transform-origin: 100% 50%;
          right: 0;
        }

        &:before {
          transform: translateY(1px) rotate(45deg);
        }

        &:after {
          transform: rotate(-45deg);
        }
      }
    }
  }

  &-minimal {
    .hidden-mobile {
      display: none;
    }

    .hidden-desktop-only {
      display: block!important;
    }

    .calendar-heading {
      flex-direction: column;

      .calendar-nav-top {
        align-self: center;
        left: unset;
        position: relative;
        top: unset;
        transform: unset;

        .calendar-nav-top-prev,
        .calendar-nav-top-next {
          transform: scale(.75);
        }

        .calendar-nav-top-prev {
          margin-right: 10px;
        }

        .calendar-nav-top-next {
          margin-left: 10px;
        }
      }
    }
    .calendar-period {
      width: 100%;
    }
  }
}

.loading-spinner-wrapper {
  background: rgba(255, 255, 255, .85);
  bottom: 0;
  border-radius: 5px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 100;

  .loading-spinner {
    top: 45%;

    &-content {
      font-weight: 500;
    }
  }
}

@media (max-width: 960px) {
  .calendar {
    margin: 1rem auto;
    width: 100%;

    &-heading {
      padding: 1rem;

      h1 {
        font-size: 2rem;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .calendar {
    &-heading {
      flex-direction: column;
      padding: .75rem;

      &-day {
        font-size: var(--font-size-small);
        height: 4rem;
      }

      .calendar-period {
        margin-bottom: 10px;
      }

      .calendar-nav-top {
        position: static;
        transform: unset;

        .calendar-nav-top-prev,
        .calendar-nav-top-next {
          position: relative;
        }
      }
    }
  }
}

@media (max-width: 640px) {
  .calendar {
    &-heading {
      h1 {
        font-size: 1.8rem;
      }
    }

    &-nav {
      height: 50px;

      .button {
        width: 135px;
      }
    }
  }
}
</style>
